// pages/news-and-insights/[url_slug].jsx
// MERGED: serves BOTH tag listings and post detail pages at /news-and-insights/:url_slug
import { Global, Collection } from '@sourceflow-uk/sourceflow-sdk';
import SeoHead from '../../components/SeoHead';
import { Helmet } from 'react-helmet';
import LatestNews from '../../components/LatestNews';
import { Container } from 'reactstrap';
import NewBlogDetails from '../../components/NewBlogDetails';
import NewSubBanner from '../../components/NewSubBanner';
import NewColumnWithForm from '../../components/NewColumnWithForm';
import NewBlogDetails2 from '../../components/NewBlogDetails2';
import MainBanner from '../../components/MainBanner';
import AllBlogs from '../../components/AllBlogs';
import NewFeaturedBlogs from '../../components/NewFeaturedBlogs';

// ---------- TAG VIEW ----------
function TagView({ global, page, all_news }) {
  return (
    <>
      <SeoHead
        page_name={`${page.title} | Staffing Agency | US and Canada Staffing | Bartech Staffing`}
        canonical_link={`news-and-insights/${page.url_slug}`}
      />
      <MainBanner
        global={global}
        pathPrefix={`insights_page.main_banner`}
        customClass={``}
        content={<h1>{page.title}</h1>}
        imageURL1={`/site-assets/images/main-banner.jpg`}
        imageALT1={`Main Banner`}
        imageURL2={`/site-assets/images/main-banner-overlay.jpg`}
        imageALT2={`Overlay Main Banner`}
        editableContent={false}
        displayImage1={false}
      />
      <NewFeaturedBlogs
        global={global}
        pathPrefix={`${page.url_slug}.insights_page.new_featured_blogs`}
        customClass={`py-5 grayGradientBg`}
        content={<h2>Featured {page.title}</h2>}
        post={all_news}
        socialPagePrefix={`news-and-insights/${page.url_slug}`}
      />
      <AllBlogs
        global={global}
        pathPrefix={`news_page.all_blogs`}
        customClass={`py-5 lightGreybg`}
        post={all_news}
        ParentNews={false}
        tagName={`${page.title}`}
        heading={`All ${page.title}`}
      />
      <NewColumnWithForm
        global={global}
        pathPrefix={`insights_page.new_column_with_form`}
        customClass={`pb-5`}
        heading={`How can we help you?`}
        formId={`1aa2b604-c59a-49b1-a4d6-558ecba63220`}
      />
    </>
  );
}

// ---------- POST VIEW ----------
function PostView({ global, page, all_news, related_news }) {
  const breadcrumbList = [
    {
      "@type": "ListItem",
      name: "Bartech Staffing",
      position: 1,
      item: { "@type": "Thing", url: "https://www.bartechstaffing.com/" },
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Insights",
      item: { "@type": "Thing", url: "https://www.bartechstaffing.com/news-and-insights" },
    },
  ];

  return (
    <>
      <SeoHead
        page_name={`${page.title} | Staffing Agency | US and Canada Staffing | Bartech Staffing`}
        canonical_link={`news-and-insights/${page.url_slug}`}
        og_image={page.image}
        og_img_alt={`${page.title}`}
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({ "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: breadcrumbList })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            description: page?.short_description || "",
            isFamilyFriendly: "http://schema.org/True",
            image: page?.image || "",
            dateModified: page?.updated_at || "",
            datePublished: page?.created_at || "",
            name: page?.title || "",
            headline: page?.title || "",
            articleBody: page?.content ? page.content.replace(/<[^>]*>/g, "") : "",
            publisher: {
              "@type": "Organization",
              name: "Bartech Staffing",
              sameAs: "https://www.bartechstaffing.com/",
              logo: { "@type": "ImageObject", url: "https://cdn.sourceflow.co.uk/1ww9v44umwxe2tz0ran8grtqleup" }
            },
            author: { "@type": "Person", image: page?.author_image || "", name: page?.author || "" }
          })}
        </script>
      </Helmet>

      <NewSubBanner
        global={global}
        pathPrefix={`${page.url_slug}.insights_page.main_banner`}
        customClass={``}
        content={<h1>{page.title}</h1>}
        imageURL1={`/site-assets/images/main-banner.jpg`}
        imageALT1={`Main Banner`}
        imageURL2={`/site-assets/svg/main-banner-overlay.svg`}
        imageALT2={`Overlay Main Banner`}
        displayImage1={false}
        editableContent={false}
        buttonLink={`${
          page.tag.includes("Case Study")
            ? "/news-and-insights/case-study"
            : page.tag.includes("Guides & Reports")
            ? "/news-and-insights/guides-reports"
            : "/news-and-insights"
        }`}
        buttonTitle={`${
          page.tag.includes("Case Study")
            ? "Back to Case Study"
            : page.tag.includes("Guides & Reports")
            ? "Back to Guides & Reports"
            : "Back to insights"
        }`}
        post={page}
        buttonLink2={`${page.tag.includes("Guides & Reports") && page?.form ? "#download-form" : ""}`}
        buttonTitle2={`${page.tag.includes("Guides & Reports") && page?.form ? "Download Report" : ""}`}
        ctaLink={page?.cta_link ?? null}
        ctaTitle={page?.cta_title ?? null}
      />

      {page.tag.includes("Guides & Reports") || page.tag.includes("News & Insights") ? (
        <NewBlogDetails
          global={global}
          pathPrefix={`insights_page.guides_and_reports.new_blog_details`}
          page={page}
          related_news={related_news}
          page_prefix={`news-and-insights`}
          share_heading={`Share Report`}
          className={`py-5 guides-and-reports-page`}
          formId={`${page?.form ? page.form : "66e2baf1-9beb-48f1-b0f4-62f583b838d0"}`}
          locale={``}
          successPageUrl={
            page.form_link
              ? page.form_link
              : page.form_attachment
              ? page.form_attachment
              : "/submission-success"
          }
        />
      ) : page.tag.includes("Case Study") ? (
        page.case_study_tabs?.length >= 1 && (
          <NewBlogDetails2 global={global} pathPrefix={`insights_page.new_blog_details_2`} page={page} />
        )
      ) : (
        <NewBlogDetails
          global={global}
          pathPrefix={`insights_page.new_blog_details`}
          page={page}
          related_news={related_news}
          page_prefix={`news-and-insights`}
          locale={``}
        />
      )}

      {(page.tag.includes("Guides & Reports") || page.tag.includes("Case Study")) && all_news.length >= 1 ? (
        <>
          <Container><hr /></Container>
          <LatestNews
            global={global}
            pathPrefix={`${page.url_slug}.insights_page.guides_and_reports.latest_news`}
            customClass={`py-5 white-card`}
            post={
              page.tag.includes("Guides & Reports")
                ? all_news.filter((p) => p.tag?.includes("Guides & Reports") && p.title !== page.title)
                : all_news.filter((p) => p.tag?.includes("Case Study") && p.title !== page.title)
            }
            content={<h2>{page.tag.includes("Guides & Reports") ? "Other Guides and Reports" : "Other Case Studies"}</h2>}
            buttonLink={``}
            buttonTitle={``}
            content_align={`left`}
            footer_buttonLink={`/news-and-insights${page.tag.includes("Guides & Reports") ? "/guides-reports" : "/case-study"}`}
            footer_buttonLink_title={`View All`}
          />
        </>
      ) : null}

      <NewColumnWithForm
        global={global}
        pathPrefix={`insights_page.new_column_with_form`}
        customClass={`pb-5`}
        heading={`How can we help you?`}
        formId={`1aa2b604-c59a-49b1-a4d6-558ecba63220`}
        successPageUrl={
          page.form_link
            ? page.form_link
            : page.form_attachment
            ? page.form_attachment
            : "/submission-success"
        }
      />
    </>
  );
}

// ---------- UNIFIED EXPORT ----------
export default function InsightsUnified(props) {
  return props.mode === 'post' ? <PostView {...props} /> : <TagView {...props} />;
}

// ---------- DATA ----------
export async function getStaticProps({ params }) {
  const global = (await Global()).toJson();

  // Try POST first
  let postPage = null;
  try {
    postPage = (await Collection("news")).getJsonItem(params.url_slug, "url_slug");
  } catch (_) {}

  if (postPage && postPage.url_slug) {
    const news = (await Collection("news"))
      .filter((post) => Array.isArray(post.tag?.en) && post.tag.en.some((tag) => postPage.tag?.includes(tag)));

    const all_news = news.orderBy("order").getItems().slice(0, 8);
    const related_news = news.orderBy("order").getItems().slice(0, 2);

    return {
      props: { global, mode: 'post', page: postPage, all_news, related_news },
    };
  }

  // Fallback to TAG listing (US or All)
  let tagPage = null;
  try {
    tagPage = (await Collection('news_tag')).getJsonItem(params.url_slug, 'url_slug');
  } catch (_) {}

  if (!tagPage) return { notFound: true };

  const tagName = tagPage.title;
  const newsForTag = (await Collection('news'))
    .filter((post) => post.country?.en === "United States" || post.country?.en === "All")
    .filter((post) => post.tag?.en?.includes(tagName));

  const all_news = newsForTag.orderBy((n) => Date.parse(n.date?.en), 'desc').getItems();

  return {
    props: { global, mode: 'tag', page: tagPage, all_news },
  };
}

export async function getStaticPaths() {
  const postPaths = (await Collection("news")).getPaths("url_slug");
  const tagPaths = (await Collection("news_tag")).getPaths("url_slug");

  return {
    paths: [...postPaths, ...tagPaths],
    fallback: false,
  };
}
